import { Provinces } from "../state/ProvinceProvider";

export interface Props {
  provinces?: string[],
  province?: string | null,
  loadingBehavior?: 'show' | 'hide' | 'fallback',
  fallbackRegion?: Provinces
}

export interface Region {
  name: string,
  pageSlug: string,
  provinceCode?: Provinces,
  parentRegion?: Region,
}

export const isRegionLocked = ({
  /*
   * Region-Locked means the content is hidden in your province.
   * Returns true if your selected province IS NOT in the list of provinces from the CMS. The content should be hidden.
   * Returns false if your selected province IS in the list of provinces from the CMS. The content should be shown.
   * showWhileLoading by default is false
  */
  provinces,
  province = null,
  loadingBehavior = 'show',
  fallbackRegion = Provinces.ON,
}: Props) => {
  let provinceToUse = province;
  if (!provinceToUse) {
    switch (loadingBehavior) {
      case 'hide':
        return true; // will hide the content
      case 'fallback':
        provinceToUse = fallbackRegion;
        break;
      default:
        break;
    }
  }
  if (provinceToUse && !provinces?.includes?.(provinceToUse)) {
    return true;
  }
  return false;
};

type RegionLockedContent = {
  id: string;
  provinces: string[];
} | null;

type HeaderData = {
  headerLinks: {
    id: string;
    regionLockedContent: RegionLockedContent;
  }[];
  hamburgerMenuBlade: {
    id: string;
    bladeLinks: {
      id: string;
      regionLockedContent: RegionLockedContent;
    }[];
    bladeCTAs: {
      id: string;
      regionLockedContent: RegionLockedContent;
    }[];
  };
  headerCTAs: {
    id: string;
    internal: {
      type: string;
    };
    regionLockedContent: RegionLockedContent;
  }[];
  mobileHeaderCTAs:{
    id: string;
    regionLockedContent: RegionLockedContent;
  }[];
};

type ContentfulArray = {
  linkUrl: string
  regionLockedContent: RegionLockedContent;
}[]

const filterRegionLocked = (
  regionLockedContent: { provinces: string[] } | null,
  province: string | null,
  loadingBehavior?: 'show' | 'hide' | 'fallback',
) => !regionLockedContent
   || !isRegionLocked({ provinces: regionLockedContent.provinces, province, loadingBehavior });

export const filterContentfulArray = (
  province: string | null,
  contentArray: ContentfulArray,
) => contentArray?.filter(content => filterRegionLocked(content.regionLockedContent, province));

export const filterHeaderLinksForRegionLocked = (
  province: string | null,
  headerData: HeaderData,
): HeaderData => {
  const filteredHeaderLinks = headerData?.headerLinks?.filter(
    (section) => filterRegionLocked(section.regionLockedContent, province, 'fallback'),
  );

  const filteredBladeLinks = headerData?.hamburgerMenuBlade?.bladeLinks?.filter(
    (bladeLink) => filterRegionLocked(bladeLink.regionLockedContent, province, 'fallback'),
  );
  const filteredBladeCTAs = headerData?.hamburgerMenuBlade?.bladeCTAs?.filter(
    (bladeCTA) => filterRegionLocked(bladeCTA.regionLockedContent, province, 'fallback'),
  );

  const filteredHeaderCTAs = headerData?.headerCTAs?.filter(
    (cta) => filterRegionLocked(cta.regionLockedContent, province, 'fallback'),
  );

  const filteredMobileHeaderCTAs = headerData?.mobileHeaderCTAs?.filter(
    (cta) => filterRegionLocked(cta.regionLockedContent, province, 'fallback'),
  );

  return {
    ...headerData,
    headerLinks: filteredHeaderLinks,
    hamburgerMenuBlade: {
      ...headerData.hamburgerMenuBlade,
      bladeLinks: filteredBladeLinks,
      bladeCTAs: filteredBladeCTAs,
    },
    headerCTAs: filteredHeaderCTAs,
    mobileHeaderCTAs: filteredMobileHeaderCTAs,
  };
};

export const getProvinceCode = (region: Region): Provinces => {
  const code = region?.provinceCode ?? region?.parentRegion?.provinceCode ?? 'ON' as Provinces;
  return Object.values(Provinces).includes(code) ? code : 'ON' as Provinces;
};

export const isRegionalPage = (url: string) => {
  const regex = /^\/(en|fr)\/location\/([^/]+)\/([^/]+)\/$/;
  return regex.test(url);
};
